import Button from 'components/Forms/Button/Button';
import { DEFAULT_PAGE_SIZE } from 'config/constants';
import { getUrlQueriesWithoutDynamicPageQueries } from 'helpers/parsing/urlParsing';
import { useCurrentLoadMoreQuery } from 'helpers/queryParams/useCurrentLoadMoreQuery';
import { useCurrentPageQuery } from 'helpers/queryParams/useCurrentPageQuery';
import { useUpdateLoadMoreQuery } from 'helpers/queryParams/useUpdateLoadMoreQuery';
import { useUpdatePaginationQuery } from 'helpers/queryParams/useUpdatePaginationQuery';
import { useMediaMin } from 'hooks/ui/useMediaMin';
import { usePagination } from 'hooks/ui/usePagination';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { ArrowIcon, SpinnerIcon } from 'public/svg/IconsSvg';
import { Fragment, RefObject, useEffect, useState } from 'react';
import twMerge from 'utils/twMerge';

type PaginationProps = {
    paginationScrollTargetRef: RefObject<HTMLDivElement> | null;
    isBlogCategory?: boolean;
    isLoading?: boolean;
    isWithLoadMore?: boolean;
    totalCount: number;
    pageSize?: number;
    onPageChangeEvent?: (newPage: number) => void;
    pushPageChangeToQueryParams?: boolean;
};

const isDotKey = (prevPage: number | null, currentPage: number): boolean => {
    return prevPage !== null && prevPage !== currentPage - 1;
};

const paginationButtonTwClass =
    'rounded-md flex h-11 w-11 items-center justify-center border border-border bg-white text-h6 font-normal no-underline hover:cursor-pointer hover:no-underline';

const Pagination: FC<PaginationProps> = ({
    paginationScrollTargetRef,
    isBlogCategory,
    isLoading,
    isWithLoadMore,
    totalCount,
    pageSize = DEFAULT_PAGE_SIZE,
    onPageChangeEvent,
    pushPageChangeToQueryParams = true,
}) => {
    const router = useRouter();
    const { t } = useTranslation();
    const currentQueryPage = useCurrentPageQuery();
    const updatePagination = useUpdatePaginationQuery();
    const currentLoadMore = useCurrentLoadMoreQuery();
    const loadMore = useUpdateLoadMoreQuery();
    const [currentPage, setCurrentPage] = useState(currentQueryPage ? currentQueryPage : 1);

    const currentPageWithLoadMore = Math.min(currentPage + currentLoadMore, Math.ceil(totalCount / pageSize));
    const isDesktop = useMediaMin('sm');

    const paginationButtons = usePagination(totalCount, currentPageWithLoadMore, !isDesktop, pageSize);

    const asPathWithoutQueryParams = router.asPath.split('?')[0];
    const queryParams = getUrlQueriesWithoutDynamicPageQueries(router.query);

    useEffect(() => {
        if (currentQueryPage > 0) {
            setCurrentPage(currentQueryPage);
        }
    }, [currentQueryPage]);

    const onChangePage = (pageNumber: number) => {
        if (paginationScrollTargetRef?.current) {
            paginationScrollTargetRef.current.scrollIntoView();
        }

        setCurrentPage(pageNumber);

        if (onPageChangeEvent) {
            onPageChangeEvent(pageNumber);
        }

        if (pushPageChangeToQueryParams) {
            updatePagination(pageNumber);
        } else {
            setCurrentPage(pageNumber);
        }
    };

    if (paginationButtons === null || paginationButtons.length === 1) {
        return null;
    }

    return (
        <div className="relative flex flex-col items-center">
            <div className="my-2 mx-auto flex w-full justify-center gap-1 sm:w-[335px]">
                {paginationButtons.map((pageNumber, index, array) => {
                    const urlPageNumber = pageNumber > 1 ? pageNumber.toString() : undefined;
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { page, ...queryParamsWithoutPage } = queryParams;
                    const pageParams = urlPageNumber
                        ? new URLSearchParams({ ...queryParams, page: urlPageNumber }).toString()
                        : new URLSearchParams(queryParamsWithoutPage).toString();
                    const pageHref = `${asPathWithoutQueryParams}${pageParams ? `?${pageParams}` : ''}`;

                    return (
                        <Fragment key={pageNumber}>
                            {isDotKey(array[index - 1] ?? null, pageNumber) && (
                                <div className={twMerge(paginationButtonTwClass, 'hover:cursor-default')}>&#8230;</div>
                            )}
                            {currentPageWithLoadMore === pageNumber ? (
                                <div
                                    className={twMerge(
                                        paginationButtonTwClass,
                                        'border-none bg-primary text-white hover:cursor-default hover:text-white',
                                    )}
                                >
                                    {pageNumber}
                                </div>
                            ) : (
                                <a
                                    tabIndex={0}
                                    href={pageHref}
                                    className={paginationButtonTwClass}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onChangePage(pageNumber);
                                    }}
                                >
                                    {pageNumber}
                                </a>
                            )}
                        </Fragment>
                    );
                })}
            </div>

            {isWithLoadMore &&
                totalCount > 0 &&
                (isLoading === true ? (
                    <Button className="my-5" type="button" size="small" isDisabled>
                        <SpinnerIcon />
                        <span>{t('loading...')}</span>
                    </Button>
                ) : (
                    paginationButtons[paginationButtons.length - 1] !== currentPageWithLoadMore && (
                        <Button className="my-5" type="button" size="small" onClick={loadMore}>
                            <ArrowIcon />
                            <span>{isBlogCategory ? t('Load more articles') : t('Load more products')}</span>
                        </Button>
                    )
                ))}
        </div>
    );
};

export default Pagination;
